/* ProductVariantDetails - Shared product gallery, purchase details, and responsive purchase bar for variant and simple products.
   Updated: Kluit/Pot type badges now use transparent background with green border and black text. Removed inline Omschrijving card — description now shown as a standalone section in the page layout. Product variant title is rendered as a bold nested H1 with the scientific name shown beneath in small italic text.
   Updated: Added DataFast add_to_cart goal. */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, ChevronLeft, ChevronRight, Plus, Minus, ChevronDown, FileText, CalendarClock, Heart, Package, Phone, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import VariantSelector from '@/components/product/VariantSelector';
import PriceMatchModal from '@/components/product/PriceMatchModal';
import { useCart } from '@/contexts/CartContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { toast } from 'sonner';
import { isAvailableFromFuture, formatDutchDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { trackGoal } from '@/lib/datafast';

interface Product {
  name: string;
  scientific_name: string;
  description: string;
  geslacht?: string;
  id?: string;
  subcategory_id?: string | null;
}

interface ProductVariant {
  id: string;
  size: string;
  type: string;
  price: string;
  price_tier_1?: number;
  price_tier_2?: number | null;
  price_tier_3?: number | null;
  price_tier_4?: number | null;
  stock_quantity: number;
  sku: string;
  images: string[];
  planten_per_meter?: number | null;
  minimum_order_quantity?: number;
  supplier?: string;
  available_from?: string | null;
}

interface RelatedVariant {
  id: string;
  size: string;
  type: string;
  price: string;
  stock_quantity: number;
  sku: string;
  slug: string;
}

interface VariantSelectorOption {
  id: string;
  size: string;
  type: string;
  slug: string;
  stock_quantity: number;
}

interface ProductVariantDetailsProps {
  product: Product;
  variant: ProductVariant;
  relatedVariants?: RelatedVariant[];
  allVariants?: VariantSelectorOption[];
  productSlug?: string;
  categorySlug?: string;
  isSimpleProduct?: boolean;
}

export default function ProductVariantDetails({ product, variant, relatedVariants = [], allVariants = [], productSlug = '', categorySlug, isSimpleProduct = false }: ProductVariantDetailsProps) {
  const productName = product.geslacht || 'deze plant';
  const plantenPerMeter = variant.planten_per_meter || 0;
  const hasMetersCalculation = plantenPerMeter > 0;
  const minQty = isSimpleProduct ? 1 : variant.minimum_order_quantity || 25;
  const initialMeters = hasMetersCalculation ? Math.ceil(minQty / plantenPerMeter) : 1;

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [meters, setMeters] = useState<number>(initialMeters);
  const [quantity, setQuantity] = useState(minQty);
  const [isAdding, setIsAdding] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [avgRating, setAvgRating] = useState<number>(0);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [priceMatchOpen, setPriceMatchOpen] = useState(false);
  const { addItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();

  const calculateQuantityFromMeters = (meterValue: number): number => {
    if (!hasMetersCalculation) return quantity;
    return Math.ceil(meterValue * plantenPerMeter);
  };

  useEffect(() => {
    if (hasMetersCalculation) {
      const calculated = calculateQuantityFromMeters(meters);
      if (calculated >= minQty) {
        setQuantity(calculated);
      } else {
        setMeters(initialMeters);
        setQuantity(minQty);
      }
    }
  }, [meters, hasMetersCalculation, minQty, initialMeters]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 360);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('stars')
          .eq('is_published', true);
        if (error) throw error;
        if (data && data.length > 0) {
          const avg = data.reduce((sum: number, r: any) => sum + r.stars, 0) / data.length;
          setAvgRating(avg);
          setReviewCount(data.length);
        }
      } catch {
        // silently skip
      }
    };
    fetchRating();
  }, []);

  const images = variant.images.length > 0 ? variant.images : [];
  const price = parseFloat(variant.price);
  const inStock = variant.stock_quantity > 0;
  const isNotYetAvailable = isAvailableFromFuture(variant.available_from);
  const favKey = `${productSlug}-${variant.id}`;
  const isFav = isFavorite(favKey);

  const getPriceForQuantity = (qty: number): number => {
    const isKaleWortel = variant.type === 'Kale wortel';
    if (isKaleWortel) {
      if (qty >= 500 && variant.price_tier_4) return variant.price_tier_4;
      if (qty >= 300 && variant.price_tier_3) return variant.price_tier_3;
      if (qty >= 200 && variant.price_tier_2) return variant.price_tier_2;
      if (qty >= 100 && variant.price_tier_1) return variant.price_tier_1;
      return price;
    } else {
      if (qty >= 101 && variant.price_tier_4) return variant.price_tier_4;
      if (qty >= 76 && variant.price_tier_3) return variant.price_tier_3;
      if (qty >= 51 && variant.price_tier_2) return variant.price_tier_2;
      return variant.price_tier_1 || price;
    }
  };

  const currentUnitPrice = getPriceForQuantity(quantity);
  const hasGraduatedPricing = !!(variant.price_tier_2 || variant.price_tier_3 || variant.price_tier_4);

  const nextImage = () => setSelectedImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const handleAddToCart = async () => {
    const minQty = isSimpleProduct ? 1 : variant.minimum_order_quantity || 25;
    if (quantity < minQty) {
      toast.error(`Minimale bestelhoeveelheid is ${minQty} stuks`);
      setQuantity(minQty);
      if (hasMetersCalculation && plantenPerMeter > 0) setMeters(minQty / plantenPerMeter);
      return;
    }
    setIsAdding(true);
    try {
      await addItem(
        isSimpleProduct
          ? {
              product_id: product.id,
              product_variant_id: null,
              product_name: product.name,
              variant_size: '',
              unit_price: currentUnitPrice,
              image_url: images[0],
              slug: productSlug,
              quantity,
              is_simple_product: true,
              category_slug: categorySlug,
            }
          : {
              product_variant_id: variant.id,
              product_name: product.name,
              variant_size: variant.size,
              variant_type: variant.type as 'Kluit' | 'Pot' | 'Kale wortel',
              unit_price: currentUnitPrice,
              image_url: images[0],
              slug: productSlug,
              quantity,
            },
      );
      if (hasMetersCalculation) setMeters(initialMeters);
      else setQuantity(minQty);
      trackGoal('add_to_cart', { product_name: product.name, variant_size: variant.size, quantity });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite({
      id: favKey,
      product_name: isSimpleProduct ? product.name : `${product.name} ${variant.size}`,
      variant_size: isSimpleProduct ? '' : variant.size,
      unit_price: price,
      image_url: images[0],
      slug: productSlug,
    });
  };

  const incrementQuantity = () => {
    if (quantity < variant.stock_quantity) setQuantity(q => q + 1);
  };

  const decrementQuantity = () => {
    const minQty = isSimpleProduct ? 1 : variant.minimum_order_quantity || 25;
    if (quantity > minQty) setQuantity(q => Math.max(minQty, q - 1));
  };

  const btnBase = "group inline-flex min-h-12 items-center justify-center gap-3 rounded-md px-6 text-sm font-semibold transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-7";

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
        {/* Image Gallery — vertical thumbnails on desktop, horizontal on mobile */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex sm:flex-col gap-3 p-2 sm:max-h-[560px] sm:overflow-y-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`relative aspect-square w-20 sm:w-20 flex-shrink-0 overflow-hidden rounded-lg ring-1 transition-all duration-200 ${
                    index === selectedImageIndex
                      ? 'ring-2 ring-[#1e2b22] ring-offset-2 ring-offset-[#fcfaf6]'
                      : 'ring-[#e7e1d6] hover:ring-[#1e2b22]/30'
                  }`}
                >
                  <img src={image} alt={`${product.name} thumbnail ${index + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Main image */}
          <div className="relative flex-1 aspect-square overflow-hidden rounded-xl">
            {images.length > 0 ? (
              <>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedImageIndex}
                    src={images[selectedImageIndex]}
                    alt={`${product.name} - ${variant.size}`}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-[background-color,transform] duration-300 hover:bg-[#e7e1d6]"
                      aria-label="Vorige afbeelding"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-[background-color,transform] duration-300 hover:bg-[#e7e1d6]"
                      aria-label="Volgende afbeelding"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`h-2 rounded-full transition-all ${index === selectedImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'}`}
                          aria-label={`Ga naar afbeelding ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#e7e1d6]">
                <Package className="h-16 w-16 text-[#cfcac2]" />
              </div>
            )}
          </div>
        </div>

        {/* Product Info & Purchase */}
        <div className="space-y-5 lg:pt-1">
          {/* Title + rating */}
          <div>
            <h1 className="max-w-xl text-2xl font-semibold leading-[1.1] tracking-[-0.03em] text-[#1e2b22] sm:text-3xl lg:text-[2.25rem]">
              <h1 className="block font-bold">{product.name} {variant.size} {variant.type}</h1>
              {product.scientific_name && (
                <span className="mt-2 block text-sm font-normal italic leading-normal text-[#5a6552] sm:text-base">
                  {product.scientific_name}
                </span>
              )}
            </h1>

            {reviewCount > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className={`w-4 h-4 ${i < Math.round(avgRating) ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`} viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-[#5a6552]">{avgRating.toFixed(1)} ({reviewCount} reviews)</span>
              </div>
            )}
          </div>

          {/* Compact badges */}
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-white bg-[#1e2b22] rounded-[5px]">
              {isSimpleProduct ? product.name : variant.size}
            </span>
            {!isSimpleProduct && (
              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-[5px] ring-1 ${variant.type === 'Kale wortel' ? 'bg-[#1e2b22] text-white ring-[#1e2b22]' : 'bg-transparent text-black ring-[#1e2b22]'}`}>
                {variant.type}
              </span>
            )}
            {inStock ? (
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-[#5a6552] bg-transparent ring-1 ring-[#5a6552] rounded-[5px]">
                Op voorraad
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 ring-1 ring-red-200 rounded-[5px]">
                Niet op voorraad
              </span>
            )}
          </div>

          {/* Variant Selector */}
          {!isSimpleProduct && allVariants.length > 1 && (
            <VariantSelector variants={allVariants} currentVariantId={variant.id} />
          )}

          {/* Price */}
          <div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-semibold tracking-[-0.02em] text-[#1e2b22] sm:text-4xl">
                {price > 0 ? `€${currentUnitPrice.toFixed(2)}` : 'Prijs op aanvraag'}
              </p>
              {price > 0 && <span className="text-sm text-[#5a6552]">per stuk</span>}
            </div>
            <p className="text-xs text-[#5a6552] mt-0.5">Prijzen zijn inclusief BTW</p>
          </div>

          {/* Meter and Quantity Selector */}
          {inStock && price > 0 && categorySlug !== 'bomen' && !isNotYetAvailable && (
            <div className="space-y-4">
              {hasMetersCalculation && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#1e2b22]">Meters</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setMeters(prev => Math.max(0.5, prev - 0.5))} disabled={meters <= 0.5} className="flex h-11 w-11 items-center justify-center rounded-md bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-colors hover:bg-[#e7e1d6] disabled:opacity-40">
                      <Minus className="h-4 w-4" />
                    </button>
                    <input type="number" value={meters} onChange={(e) => { const val = parseFloat(e.target.value); if (val >= 0.5) setMeters(val); }} className="w-20 rounded-md border border-[#e7e1d6] bg-white py-2.5 text-center text-lg font-semibold text-[#1e2b22]" min="0.5" step="0.5" />
                    <button onClick={() => setMeters(prev => prev + 0.5)} className="flex h-11 w-11 items-center justify-center rounded-md bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-colors hover:bg-[#e7e1d6]">
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-[#5a6552]">{plantenPerMeter} planten/meter</span>
                  </div>
                </div>
              )}
              <div>
                <label className="mb-2 block text-sm font-semibold text-[#1e2b22]">Aantal</label>
                <div className="flex items-center gap-3">
                  <button onClick={decrementQuantity} disabled={quantity <= (isSimpleProduct ? 1 : variant.minimum_order_quantity || 25)} className="flex h-11 w-11 items-center justify-center rounded-md bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-colors hover:bg-[#e7e1d6] disabled:opacity-40">
                    <Minus className="h-4 w-4" />
                  </button>
                  <input type="number" value={quantity} onChange={(e) => { const val = parseInt(e.target.value); if (!isNaN(val)) { setQuantity(val); if (hasMetersCalculation && plantenPerMeter > 0) setMeters(val / plantenPerMeter); } }} onBlur={(e) => { const val = parseInt(e.target.value); const minQty = isSimpleProduct ? 1 : variant.minimum_order_quantity || 25; if (isNaN(val) || val < minQty) { setQuantity(minQty); if (hasMetersCalculation && plantenPerMeter > 0) setMeters(minQty / plantenPerMeter); } else if (val > variant.stock_quantity) { setQuantity(variant.stock_quantity); if (hasMetersCalculation && plantenPerMeter > 0) setMeters(variant.stock_quantity / plantenPerMeter); } }} className="w-20 rounded-md border border-[#e7e1d6] bg-white py-2.5 text-center text-lg font-semibold text-[#1e2b22]" min={isSimpleProduct ? 1 : variant.minimum_order_quantity || 25} max={variant.stock_quantity} />
                  <button onClick={incrementQuantity} disabled={quantity >= variant.stock_quantity} className="flex h-11 w-11 items-center justify-center rounded-md bg-white text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-colors hover:bg-[#e7e1d6] disabled:opacity-40">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Graduated Pricing Table */}
          {hasGraduatedPricing && (
            <div className="rounded-xl bg-white p-5 ring-1 ring-[#e7e1d6]">
              <div className="pb-3 mb-2 border-b border-[#e7e1d6]">
                <h3 className="text-sm font-semibold text-[#1e2b22]">Staffelprijzen</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="py-2.5 text-left font-medium text-[#5a6552]">Aantal</th>
                    <th className="py-2.5 text-right font-medium text-[#5a6552]">Prijs per stuk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e7e1d6]/60">
                  {variant.type === 'Kale wortel' ? (
                    <>
                      <tr className={quantity >= 100 && quantity <= 199 ? 'font-semibold' : ''}>
                        <td className="py-2.5 text-[#5a6552]">100 - 199 stuks</td>
                        <td className="py-2.5 text-right text-[#1e2b22]">€{(variant.price_tier_1 || price).toFixed(2)}</td>
                      </tr>
                      {variant.price_tier_2 && (
                        <tr className={quantity >= 200 && quantity <= 299 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">200 - 299 stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_2.toFixed(2)}</td>
                        </tr>
                      )}
                      {variant.price_tier_3 && (
                        <tr className={quantity >= 300 && quantity <= 499 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">300 - 499 stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_3.toFixed(2)}</td>
                        </tr>
                      )}
                      {variant.price_tier_4 && (
                        <tr className={quantity >= 500 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">500+ stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_4.toFixed(2)}</td>
                        </tr>
                      )}
                    </>
                  ) : (
                    <>
                      <tr className={quantity >= 25 && quantity <= 50 ? 'font-semibold' : ''}>
                        <td className="py-2.5 text-[#5a6552]">25 - 50 stuks</td>
                        <td className="py-2.5 text-right text-[#1e2b22]">€{(variant.price_tier_1 || price).toFixed(2)}</td>
                      </tr>
                      {variant.price_tier_2 && (
                        <tr className={quantity >= 51 && quantity <= 75 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">51 - 75 stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_2.toFixed(2)}</td>
                        </tr>
                      )}
                      {variant.price_tier_3 && (
                        <tr className={quantity >= 76 && quantity <= 100 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">76 - 100 stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_3.toFixed(2)}</td>
                        </tr>
                      )}
                      {variant.price_tier_4 && (
                        <tr className={quantity >= 101 ? 'font-semibold' : ''}>
                          <td className="py-2.5 text-[#5a6552]">101+ stuks</td>
                          <td className="py-2.5 text-right text-[#1e2b22]">€{variant.price_tier_4.toFixed(2)}</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Primary CTA row: add to cart + favorite */}
          <div className="flex gap-3">
            {categorySlug === 'bomen' ? (
              <a
                href="tel:+31850233564"
                className={`${btnBase} flex-1 bg-white text-[#1e2b22] ring-1 ring-inset ring-[#1e2b22]/20 hover:bg-[#e7e1d6] py-5 text-base`}
              >
                <ShoppingCart className="h-5 w-5" />
                Contact opnemen
              </a>
            ) : isNotYetAvailable ? (
              <div className={`${btnBase} flex-1 bg-[#fcfaf6] text-[#1e2b22] ring-1 ring-[#e7e1d6] py-5 text-base pointer-events-none`}>
                <CalendarClock className="h-5 w-5" />
                Leverbaar vanaf: {formatDutchDate(variant.available_from)}
              </div>
            ) : inStock && price > 0 ? (
              <button
                onClick={handleAddToCart}
                disabled={isAdding}
                className={`${btnBase} flex-1 bg-[#1e2b22] text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] hover:bg-[#5a6552] py-5 text-base`}
              >
                {isAdding ? 'Toevoegen...' : (<><ShoppingCart className="h-5 w-5" /> In winkelwagen</>)}
              </button>
            ) : (
              <a
                href={`https://wa.me/31850233564?text=${encodeURIComponent(`Hallo, ik heb interesse in ${product.name} ${variant.size} ${variant.type}. Graag ontvang ik een projectprijs.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${btnBase} flex-1 bg-[#1e2b22] text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] hover:bg-[#5a6552] py-5 text-base`}
              >
                <ShoppingCart className="h-5 w-5" />
                Vraag projectprijs aan
              </a>
            )}
            <button
              onClick={handleToggleFavorite}
              className="flex min-h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-md bg-white text-red-600 ring-1 ring-[#e7e1d6] transition-colors duration-200 hover:bg-white"
              aria-label={isFav ? 'Verwijder uit favorieten' : 'Voeg toe aan favorieten'}
            >
              <Heart className={`h-5 w-5 text-red-600 ${isFav ? 'fill-red-600' : ''}`} />
            </button>
          </div>

          {/* Secondary CTA: project price */}
          <a
            href="/projectprijs-aanvragen"
            className={`${btnBase} w-full bg-[#5a6552] text-white hover:bg-[#1e2b22] py-4 text-sm`}
          >
            <FileText className="h-4 w-4" />
            Projectprijs aanvragen
          </a>

          {/* Price match CTA */}
          <button
            type="button"
            onClick={() => setPriceMatchOpen(true)}
            className="flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-[#5a6552] underline transition-colors hover:text-[#1e2b22]"
          >
            <Tag className="h-4 w-4" />
            Elders goedkoper?
          </button>

        </div>
      </div>

      {/* Scroll purchase bar */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 border-t border-[#e7e1d6] bg-white/95 px-4 py-3 shadow-[0_-8px_30px_rgba(30,43,34,0.10)] backdrop-blur transition-all duration-300 sm:px-6 ${
          isScrolled ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'
        }`}
        aria-hidden={!isScrolled}
      >
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-xs text-[#5a6552] sm:text-sm">{isSimpleProduct ? product.name : `${product.name} ${variant.size}`}</p>
            <p className="text-xl font-semibold tracking-[-0.02em] text-[#1e2b22] sm:text-2xl">
              {price > 0 ? `€${currentUnitPrice.toFixed(2)}` : 'Prijs op aanvraag'}
              {price > 0 && <span className="ml-1 text-xs font-normal text-[#5a6552] sm:text-sm">per stuk</span>}
            </p>
          </div>
          {categorySlug === 'bomen' || !inStock || price <= 0 ? (
            <div className="flex shrink-0 items-center gap-2">
              {categorySlug === 'bomen' && (
                <a
                  href="tel:+31850233564"
                  aria-label="Bel ons"
                  className="inline-flex min-h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 border-[#5a6552] bg-white text-[#5a6552] transition-colors hover:bg-[#5a6552] hover:text-white"
                >
                  <Phone className="h-5 w-5" />
                </a>
              )}
              <a
                href={`https://wa.me/31850233564?text=${encodeURIComponent(`Hallo, ik heb interesse in ${product.name} ${variant.size} ${variant.type}. Graag ontvang ik een projectprijs.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-md bg-[#1e2b22] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#5a6552] sm:px-7"
              >
                Projectprijs aanvragen
              </a>
            </div>
          ) : isNotYetAvailable ? (
            <span className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-md bg-[#e7e1d6] px-5 text-sm font-semibold text-[#1e2b22] sm:px-7">
              Leverbaar vanaf {formatDutchDate(variant.available_from)}
            </span>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-[#1e2b22] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#5a6552] disabled:cursor-wait disabled:opacity-70 sm:px-7"
            >
              <ShoppingCart className="h-4 w-4" />
              {isAdding ? 'Toevoegen...' : 'In winkelwagen'}
            </button>
          )}
        </div>
      </div>

      <PriceMatchModal
        open={priceMatchOpen}
        onOpenChange={setPriceMatchOpen}
        productName={product.name}
        variantSize={variant.size}
        variantType={variant.type}
        variantId={variant.id}
        ourPrice={currentUnitPrice}
      />
    </>
  );
}
